import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Description as FileIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { EasyCheckButton } from '../../common/Controls';
import { bankImportService } from '../../../services/banks/bankImportService';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadArea = styled(Box)(({ theme, isDragOver }) => ({
  border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragOver ? theme.palette.primary.light + '10' : theme.palette.grey[50],
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '10',
  }
}));

const BankImportModal = ({ open, onClose, onImportComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0, status: '' });
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // eslint-disable-next-line no-unused-vars
  const steps = [
    'Upload File',
    'Import Progress',
    'Import Results'
  ];

  const handleClose = () => {
    if (!isProcessing) {
      setActiveStep(0);
      setSelectedFile(null);
      setImportResults(null);
      setError(null);
      setProgress({ current: 0, total: 0, percentage: 0, status: '' });
      onClose();
    }
  };

  const handleDownloadTemplate = () => {
    try {
      bankImportService.downloadTemplate();
    } catch (error) {
      setError('Failed to download template: ' + error.message);
    }
  };

  const handleFileSelect = (file) => {
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select a valid Excel file (.xlsx)');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);
    setActiveStep(1);
    
    try {
      const results = await bankImportService.importBanks(selectedFile, (progressData) => {
        setProgress(progressData);
      });

      setImportResults(results);
      setActiveStep(2);
      
      if (onImportComplete) {
        onImportComplete(results);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadResults = () => {
    if (importResults) {
      bankImportService.downloadResultFile(importResults.results, selectedFile.name);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS': return 'success';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS': return <SuccessIcon fontSize="small" />;
      case 'FAILED': return <ErrorIcon fontSize="small" />;
      default: return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          Bank Master Import
        </Typography>
        <IconButton onClick={handleClose} disabled={isProcessing}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FileIcon color="primary" />
              <Typography variant="h6">Excel Template (Optional)</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Download the Excel template if you need the correct format. The template includes only the required fields.
            </Typography>
            
            <List dense sx={{ mb: 2 }}>
              <ListItem>
                <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Required Fields" 
                  secondary="Account Number, Account Name, Currency"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Supported Currencies" 
                  secondary="AED, OMR, SAR, BHD, KWD, QAR, EGP, EUR"
                />
              </ListItem>
            </List>

            <EasyCheckButton
              buttonType="action"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              size="small"
            >
              Download Template
            </EasyCheckButton>
          </CardContent>
        </Card>

        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>Upload File</StepLabel>
            <StepContent>
              <UploadArea
                isDragOver={isDragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 2 }}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {selectedFile ? selectedFile.name : 'Upload Excel File'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Drag and drop your Excel file here, or click to browse
                </Typography>
                <VisuallyHiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                />
              </UploadArea>

              {selectedFile && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FileIcon color="success" />
                      <Box>
                        <Typography variant="subtitle2">{selectedFile.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              <EasyCheckButton
                buttonType="action"
                variant="contained"
                onClick={handleImport}
                disabled={!selectedFile}
                loading={isProcessing}
                startIcon={<UploadIcon />}
                sx={{ mt: 1 }}
              >
                Start Import
              </EasyCheckButton>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Import Progress</StepLabel>
            <StepContent>
              {isProcessing && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Processing Import...
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{progress.status}</Typography>
                        <Typography variant="body2">
                          {progress.current} / {progress.total} ({progress.percentage}%)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={progress.percentage} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Please wait while we process your file. Do not close this window.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Import Results</StepLabel>
            <StepContent>
              {importResults && (
                <Box>
                  <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary" fontWeight={600}>
                          {importResults.summary.total}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Records
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main" fontWeight={600}>
                          {importResults.summary.successful}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Successful
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="error.main" fontWeight={600}>
                          {importResults.summary.failed}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Failed
                        </Typography>
                      </CardContent>
                    </Card>
                  </Stack>

                  <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Account Number</TableCell>
                          <TableCell>Account Name</TableCell>
                          <TableCell>Currency</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Message</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importResults.results.map((result) => (
                          <TableRow key={result.rowNumber}>
                            <TableCell>{result.rowNumber}</TableCell>
                            <TableCell>{result.data['Account Number']}</TableCell>
                            <TableCell>{result.data['Account Name']}</TableCell>
                            <TableCell>{result.data['Currency']}</TableCell>
                            <TableCell>
                              <Chip
                                icon={getStatusIcon(result.status)}
                                label={result.status}
                                color={getStatusColor(result.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {result.message}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <EasyCheckButton
                    buttonType="action"
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadResults}
                    sx={{ mr: 2 }}
                  >
                    Download Results
                  </EasyCheckButton>
                </Box>
              )}
            </StepContent>
          </Step>
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <EasyCheckButton 
          buttonType="form"
          variant="outlined"
          onClick={handleClose} 
          disabled={isProcessing}
        >
          {importResults ? 'Close' : 'Cancel'}
        </EasyCheckButton>
      </DialogActions>
    </Dialog>
  );
};

export default BankImportModal; 